import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Alert, Badge, Card, HelpTip, PageHeader } from '@/components/ui';
import { api } from '@/lib/api';
import { useCalendarToday } from '@/lib/home-today-store';
import { FreioOmie } from './integracoes/FreioOmie';

import { formatDataHoraSegundos } from '@/lib/format-date';
/**
 * `Configurações › Integrações › Visão geral` — **o estado de cada integração,
 * e só isso.**
 *
 * ── ⚠️ O que ela era, e por que mudou (06/09/2026)
 *
 * Era a página de tudo: o freio do Omie, a frequência, os horários da
 * conferência, a distância mínima, as empresas — **e embaixo Auvo, Google e
 * WhatsApp**, num arquivo de 592 linhas.
 *
 * O Matheus: *"essa tela tá tudo muito junto aqui — precisamos reorganizar"*.
 * 🔴 **Ninguém lia a página até o fim**, e cada integração nova empurrava as
 * outras para baixo. **Uma página que cresce por adição não é uma página, é uma
 * pilha.**
 *
 * Agora: **um cartão por integração, com o estado** — ligada ou não, quando
 * falou pela última vez, se há algo errado — **e um caminho para os controles.**
 *
 * ── 🔴 As duas exceções, e elas são deliberadas
 *
 * **Os dois freios de emergência ficam AQUI**, na visão geral, além de estarem
 * nas suas páginas. ⚠️ **São os botões que se procuram com pressa** — dar-lhes
 * um nível de navegação seria o oposto do que uma parada de emergência precisa
 * ser.
 *
 * Não são cópias: o freio do Omie é o mesmo `<FreioOmie/>` que a página dele
 * usa, lendo o mesmo endpoint. **Dois blocos de JSX iguais divergiriam no dia em
 * que alguém mexesse num só.**
 */

// ─── Os estados que os cartões leem ─────────────────────────────────────────

interface OmieSyncStatus {
  writeMode: 'disabled' | 'dry_run' | 'live';
  empresasComCredenciais: string[];
  ultimosSync: Array<{ ultimo_finalizado: string }>;
}

interface AuvoStatus {
  config?: { ligada?: boolean; temCredencial?: boolean };
  ultimaLeitura?: { completadoEmIso?: string | null; resultado?: string | null } | null;
}

interface WhatsAppStatus {
  freioPuxado?: boolean;
  temCredencial?: boolean;
  gatilhosLigados?: number;
  ultimoEnvioIso?: string | null;
}

const AJUDA_FILAS =
  'Estas três não são integrações: são trabalho que as integrações produzem e que só uma pessoa ' +
  'pode terminar. Casar um cadastro de fora com um do sistema, decidir se um produto do Omie vira ' +
  'item do catálogo, conferir por que um produto sumiu. Ficam aqui porque é aqui que alguém as ' +
  'procura — e o sistema nunca decide nenhuma delas sozinho.';

function quando(iso: string | null | undefined): string {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'nunca' : formatDataHoraSegundos(d);
}

/**
 * O cartão de uma integração.
 *
 * ⚠️ Ele mostra **estado**, nunca controle — com a exceção dos freios, que
 * entram como `extra` por decisão explícita.
 */
function CartaoEstado({
  nome,
  descricao,
  estado,
  variante,
  detalhe,
  para,
  testid,
  extra,
}: {
  nome: string;
  descricao: string;
  estado: string;
  variante: 'success' | 'warning' | 'danger' | 'neutral';
  detalhe: string;
  para: string;
  testid: string;
  extra?: ReactNode;
}) {
  return (
    <Card className="p-4 space-y-3" data-testid={testid}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-lg text-ink-1 dark:text-ink-dark-1">{nome}</h2>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3">{descricao}</p>
        </div>
        <Badge variant={variante}>{estado}</Badge>
      </div>

      <p className="text-sm text-ink-2 dark:text-ink-dark-2">{detalhe}</p>

      {extra}

      <Link
        to={para}
        className="inline-block text-sm text-brand underline underline-offset-2"
        data-testid={`${testid}-configurar`}
      >
        Configurar
      </Link>
    </Card>
  );
}

export function IntegracoesPage() {
  const [omie, setOmie] = useState<OmieSyncStatus | null>(null);
  const [omieErr, setOmieErr] = useState<string | null>(null);
  const [auvo, setAuvo] = useState<AuvoStatus | null>(null);
  const [wa, setWa] = useState<WhatsAppStatus | null>(null);
  const calendar = useCalendarToday();

  useEffect(() => {
    api
      .get<OmieSyncStatus>('/api/v1/admin/omie/sync/status')
      .then(setOmie)
      .catch((e: Error) => setOmieErr(e.message));
    // ⚠️ Auvo e WhatsApp não derrubam a página se falharem: o cartão diz que
    // não conseguiu falar, e os outros continuam de pé.
    api.get<AuvoStatus>('/api/v1/admin/auvo/status').then(setAuvo).catch(() => setAuvo({}));
    api.get<WhatsAppStatus>('/api/v1/admin/whatsapp/status').then(setWa).catch(() => setWa({}));
  }, []);

  const ultimoOmie = (omie?.ultimosSync ?? [])
    .map((u) => u.ultimo_finalizado)
    .filter((v): v is string => Boolean(v))
    .sort()
    .pop();

  const google = calendar.notConfigured
    ? { estado: 'Não configurada', variante: 'neutral' as const, detalhe: 'A conta de serviço do Google ainda não foi configurada neste ambiente.' }
    : calendar.scopeInsufficient
      ? { estado: 'Falta autorizar', variante: 'warning' as const, detalhe: 'O administrador do Google Workspace precisa autorizar a leitura da agenda.' }
      : calendar.isError
        ? { estado: 'Erro de conexão', variante: 'danger' as const, detalhe: 'Não foi possível consultar a agenda do Google.' }
        : calendar.isLoading || calendar.isFetching
          ? { estado: 'Verificando…', variante: 'neutral' as const, detalhe: 'Consultando a agenda do Google…' }
          : { estado: 'Conectada', variante: 'success' as const, detalhe: 'A agenda do Google está respondendo normalmente.' };

  return (
    <section className="min-w-0 space-y-6" data-testid="integracoes-page">
      <PageHeader
        className="mb-0"
        crumb="Configurações · Integrações"
        title="Integrações"
        subtitle="Os sistemas de fora que conversam com o IGM. Aqui você vê como cada um está agora; os controles de cada um ficam na página dele."
      />

      {omieErr && (
        <Alert variant="danger" rail title="Não consegui falar com o Omie">
          {omieErr}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <CartaoEstado
          testid="cartao-omie"
          nome="Omie"
          descricao="Sistema financeiro — clientes, produtos, pedidos e contas."
          estado={omie ? `${omie.empresasComCredenciais.length} empresa(s) com acesso` : 'Carregando…'}
          variante={omie ? (omie.empresasComCredenciais.length > 0 ? 'success' : 'warning') : 'neutral'}
          detalhe={`Última sincronização: ${quando(ultimoOmie)}.`}
          para="/admin/configuracoes/integracoes/omie"
          /* 🔴 O freio fica aqui: é o botão que se procura com pressa. */
          extra={<FreioOmie compacto />}
        />

        <CartaoEstado
          testid="cartao-auvo"
          nome="Auvo"
          descricao="Ordens de serviço em campo. O IGM apenas LÊ."
          estado={auvo?.config?.ligada ? 'Ligada' : 'Desligada'}
          variante={auvo?.config?.ligada ? 'success' : 'neutral'}
          detalhe={
            auvo?.config?.temCredencial === false
              ? 'Sem chave de acesso — ela é digitada na página do Auvo.'
              : `Última leitura: ${quando(auvo?.ultimaLeitura?.completadoEmIso)}.`
          }
          para="/admin/configuracoes/integracoes/auvo"
        />

        <CartaoEstado
          testid="cartao-whatsapp"
          nome="WhatsApp"
          descricao="Avisos automáticos e comunicados."
          estado={wa?.freioPuxado ? 'Freio puxado' : wa?.temCredencial ? 'Configurado' : 'Sem credencial'}
          variante={wa?.freioPuxado ? 'danger' : wa?.temCredencial ? 'success' : 'neutral'}
          detalhe={
            wa?.freioPuxado
              ? 'O freio está puxado: nenhuma mensagem de WhatsApp nem e-mail a cliente sai. Os e-mails da equipe continuam saindo.'
              : `${wa?.gatilhosLigados ?? 0} aviso(s) ligado(s). Último envio: ${quando(wa?.ultimoEnvioIso)}.`
          }
          para="/admin/configuracoes/integracoes/whatsapp"
        />

        <CartaoEstado
          testid="cartao-google"
          nome="Google Agenda"
          descricao="Leitura da agenda para o Comercial."
          estado={google.estado}
          variante={google.variante}
          detalhe={google.detalhe}
          para="/admin/configuracoes/integracoes/google"
        />
      </div>

      {/* ⚠️ Não são integrações: são filas de trabalho que nascem delas. Ficam
          aqui porque é onde alguém as procura, e continuam a um clique. */}
      <Card className="p-4 space-y-2" data-testid="cartao-filas">
        <span className="inline-flex items-center gap-1.5">
          <h2 className="font-display text-lg text-ink-1 dark:text-ink-dark-1">
            O que espera decisão
          </h2>
          <HelpTip texto={AJUDA_FILAS} />
        </span>
        <p className="text-xs text-ink-3 dark:text-ink-dark-3">
          Filas que nascem das integrações e precisam de alguém.
        </p>
        <ul className="space-y-1 text-sm">
          <li>
            <Link to="/admin/configuracoes/integracoes/vinculos" className="text-brand underline underline-offset-2">
              Vínculos de cliente
            </Link>{' '}
            — cadastros do Omie e do Auvo esperando par aqui dentro.
          </li>
          <li>
            <Link to="/admin/configuracoes/integracoes/produtos-conciliacao" className="text-brand underline underline-offset-2">
              Produtos — fila de conciliação
            </Link>{' '}
            — produtos do Omie esperando virar item do catálogo.
          </li>
          <li>
            <Link to="/admin/configuracoes/integracoes/produtos-sumidos" className="text-brand underline underline-offset-2">
              Produtos que sumiram do Omie
            </Link>{' '}
            — escondidos da busca do orçamento, e por quê.
          </li>
        </ul>
      </Card>
    </section>
  );
}
